<template>
  <div class="folder-files-item w-full flex items-center overflow-auto">
    <div class="flex items-center">
      <CFileThumbnail
        :item="
          isSlideType(item.type)
            ? ({ thumbnail_url: item.slide.url } as RenderableFile)
            : (item.file as unknown as RenderableFile)
        "
      />
    </div>

    <div class="flex flex-col justify-center gap-2 pa-3 truncate w-full">
      <div class="font-size-[18px] font-700 tracking-[0.4px] truncate line-height-[1.2]">
        <CTableInput
          :always-display-edit-icon="true"
          class="overflow-auto"
          :class="{ 'w-full max-w-[400px]': getInputState(activeInputId === item.id) === 'edit' }"
          :max-length="64"
          :model-value="
            getInputState(activeInputId === item.id) === 'view'
              ? `${slideDetails?.title} (${item.slide.index + 1})`
              : slideDetails?.title
          "
          :state="getInputState(activeInputId === item.id)"
          value-class="color-text"
          @cancel="clearActiveInput()"
          @edit="setActiveInput(item.id)"
          @update:modelValue="editItemName(item.slide.index, $event)"
        />
      </div>

      <div class="truncate line-height-[1.2] color-text2">
        <CIcon class="mr-1" icon="bring-forward" />
        <span>{{ t('canvasUI.pptConversionSelector.slide') }}</span>
      </div>
    </div>

    <div class="flex justify-center item-center flex-shrink-0 gap-2">
      <CSelect
        v-if="slideDetails?.id in existingSections"
        :bordered="true"
        label-field="name"
        :options="overwriteOptions"
        :value="existingSections[slideDetails?.id].saveAction"
        value-field="key"
        @update:value="existingSections[slideDetails?.id].saveAction = $event"
      />

      <CDropdown
        v-if="!isDragDropDisabled"
        :options="getActionsMenu()"
        :themeOverrides="dropdownThemeOverrides"
        trigger="click"
        @select="handleActions"
      >
        <CButton :circle="true">
          <CIcon icon="ellipsis" size="16px" />
        </CButton>
      </CDropdown>
    </div>
  </div>
</template>

<script lang="tsx" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import type { RenderableFile } from '@lib/types/files'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import type { CollectionPage } from '@collection-player/types/collection-player.type'
import { dropdownThemeOverrides } from '@ppt-conversion-selector/utils/theme-overrides.util'
import type { ValuesOf } from '@lib/types/common'
import { useAppStore } from '@ppt-conversion-selector/stores/app.store'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import { useTableInput } from '@ppt-conversion-selector/composables/table-input.use'
import type { SlideMetadata } from '@ppt-conversion-selector/stores/app.store'

enum ReorderMenu {
  MoveUp = 'moveUp',
  MoveDown = 'moveDown',
  MoveToGroup = 'moveToGroup',
}

const props = defineProps<{
  item: CollectionPage
  isFirst?: boolean
  isLast?: boolean
  disabledMoveToGroup?: boolean
}>()

const emit = defineEmits<{
  moveUp: []
  moveDown: []
  moveToGroup: []
}>()

const { t } = useI18n()
const { existingSections, updateSlideName, isDragDropDisabled } = useAppStore()
const { getInputState, setActiveInput, clearActiveInput, activeInputId } = useTableInput()
const slideDetails = computed<SlideMetadata>(() => props.item.file.metadata?.pptx_data.slides[props.item.slide.index])

const isSlideType = (type: string) => type === 'slide'

const editItemName = (slideIndex: number, name: string) => {
  updateSlideName(slideIndex, name)
  clearActiveInput()
}

const overwriteOptions = [
  {
    key: 'overwrite',
    name: t('canvasUI.pptConversionSelector.updateExisting'),
  },
  {
    key: 'keep',
    name: t('canvasUI.pptConversionSelector.keepExisting'),
  },
]

function handleActions(key: ValuesOf<ReorderMenu>) {
  switch (key) {
    case ReorderMenu.MoveUp:
      emit(ReorderMenu.MoveUp)
      break
    case ReorderMenu.MoveDown:
      emit(ReorderMenu.MoveDown)
      break
    case ReorderMenu.MoveToGroup:
      emit(ReorderMenu.MoveToGroup)
      break
    default:
      break
  }
}

function getActionsMenu() {
  return [
    {
      disabled: props.isFirst,
      label: () => <div class="font-size-4">{t('canvasUI.pptConversionSelector.moveItem.moveUp')}</div>,
      key: ReorderMenu.MoveUp,
      icon: () => <CIcon icon="arrow-up" />,
    },
    {
      disabled: props.isLast,
      label: () => <div class="font-size-4">{t('canvasUI.pptConversionSelector.moveItem.moveDown')}</div>,
      key: ReorderMenu.MoveDown,
      icon: () => <CIcon icon="arrow-down" />,
    },
    {
      disabled: props.disabledMoveToGroup,
      label: () => <div class="font-size-4">{t('canvasUI.pptConversionSelector.moveItem.moveToGroup')}</div>,
      key: ReorderMenu.MoveToGroup,
      icon: () => <CIcon icon="arrows-from-dotted-line" />,
    },
  ]
}
</script>
